import React, { Component } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import PrivacyData from '@/components/PrivacyData/PrivacyData';
import styles from './UserProfile.module.scss';

interface UserProfileState {
  loading: boolean;
  saving: boolean;
  error: string | null;
  successMessage: string | null;
  isOnboarding: boolean; // New user completing profile for first time
  formData: {
    first_name: string;
    last_name: string;
    display_name: string;
    tagline: string;
    phone: string;
    profile_pic: string;
    profile_mode: 'public' | 'private';
    city: string;
    state: string;
    country: string;
    preferred_game_types: string[];
  };
  errors: {
    [key: string]: string;
  };
  profilePicFile: File | null;
  profilePicPreview: string | null;
}

// Higher-order component to provide navigation
function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    return <WrappedComponent {...props} navigate={navigate} />;
  };
}

const GAME_TYPES = ['8-ball', '9-ball', '10-ball', 'snooker', 'carom'] as const;

const DEFAULT_AVATAR = 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=400';

class UserProfile extends Component<any, UserProfileState> {
  private fileInputRef: React.RefObject<HTMLInputElement>;
  private errorMessageRef: React.RefObject<HTMLDivElement>;
  private displayNameInputRef: React.RefObject<HTMLInputElement>;

  constructor(props: any) {
    super(props);
    this.fileInputRef = React.createRef();
    this.errorMessageRef = React.createRef();
    this.displayNameInputRef = React.createRef();
    this.state = {
      loading: true,
      saving: false,
      error: null,
      successMessage: null,
      isOnboarding: false,
      formData: {
        first_name: '',
        last_name: '',
        display_name: '',
        tagline: '',
        phone: '',
        profile_pic: '',
        profile_mode: 'public',
        city: '',
        state: '',
        country: '',
        preferred_game_types: ['8-ball', '9-ball']
      },
      errors: {},
      profilePicFile: null,
      profilePicPreview: null
    };
  }

  componentDidMount() {
    this.loadProfile();
  }

  loadProfile = async () => {
    try {
      this.setState({ loading: true, error: null });
      const response = await apiService.getCustomerProfile();
      
      if (response.success && response.data) {
        const profile = response.data;
        // Check if profile is complete (onboarding required)
        const hasFirstName = !!(profile.firstName || profile.first_name);
        const hasLastName = !!(profile.lastName || profile.last_name);
        const hasProfilePic = !!(profile.profilePic || profile.profile_pic);
        const hasProfileMode = !!(profile.profileMode || profile.profile_mode);
        const isOnboarding = !(hasFirstName && hasLastName && hasProfilePic && hasProfileMode);
        
        // API returns camelCase, map to snake_case for internal state
        this.setState({
          isOnboarding,
          formData: {
            first_name: profile.firstName || profile.first_name || '',
            last_name: profile.lastName || profile.last_name || '',
            display_name: profile.displayName || profile.display_name || '',
            tagline: profile.tagline || '',
            phone: profile.phone || '',
            profile_pic: profile.profilePic || profile.profile_pic || '',
            profile_mode: profile.profileMode || profile.profile_mode || 'public',
            city: profile.city || '',
            state: profile.state || '',
            country: profile.country || '',
            preferred_game_types: profile.preferredGameTypes || profile.preferred_game_types || ['8-ball', '9-ball']
          },
          profilePicPreview: profile.profilePic || profile.profile_pic || DEFAULT_AVATAR,
          loading: false
        });
      } else {
        // New user - onboarding required
        this.setState({
          isOnboarding: true,
          loading: false,
          error: null,
          profilePicPreview: DEFAULT_AVATAR
        });
      }
    } catch (error: any) {
      console.error('Failed to load profile:', error);
      // If profile doesn't exist, that's OK - user can create one (onboarding)
      this.setState({
        isOnboarding: true,
        loading: false,
        error: null,
        profilePicPreview: DEFAULT_AVATAR
      });
    }
  };

  validateName = (name: string, fieldName: string): string | null => {
    if (!name.trim()) return null; // Optional field
    
    if (name.length > 50) {
      return `${fieldName} must not exceed 50 characters`;
    }
    
    // Allow letters, spaces, hyphens, and apostrophes only
    if (!/^[a-zA-Z\s\-']+$/.test(name.trim())) {
      return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
    }
    
    return null;
  };

  validatePhone = (phone: string): string | null => {
    if (!phone.trim()) return null; // Optional field
    
    if (phone.length > 15) {
      return 'Phone number must not exceed 15 characters';
    }
    
    // Allow phone format: +1234567890, (123) 456-7890, etc.
    if (!/^\+?[\d\s\-\(\)]{10,15}$/.test(phone.trim())) {
      return 'Please enter a valid phone number (10-15 digits with optional +, spaces, hyphens, or parentheses)';
    }
    
    return null;
  };

  validateDisplayName = (name: string): string | null => {
    if (!name.trim()) return null; // Optional field
    
    if (name.length > 100) {
      return 'Display name must not exceed 100 characters';
    }
    
    // Allow letters, numbers, spaces, hyphens, apostrophes, and underscores
    if (!/^[a-zA-Z0-9\s\-'_]+$/.test(name.trim())) {
      return 'Display name can only contain letters, numbers, spaces, hyphens, apostrophes, and underscores';
    }
    
    return null;
  };

  validateField = (name: string, value: any): string | null => {
    switch (name) {
      case 'first_name':
        return this.validateName(value, 'First name');
      case 'last_name':
        return this.validateName(value, 'Last name');
      case 'display_name':
        return this.validateDisplayName(value);
      case 'phone':
        return this.validatePhone(value);
      case 'tagline':
        if (value && value.length > 200) {
          return 'Tagline must not exceed 200 characters';
        }
        return null;
      case 'city':
      case 'state':
      case 'country':
        if (value && value.length > 100) {
          return `${name.charAt(0).toUpperCase() + name.slice(1)} must not exceed 100 characters`;
        }
        // Allow letters, spaces, hyphens, apostrophes, and periods for location fields
        if (value && !/^[a-zA-Z0-9\s\-'.,]+$/.test(value.trim())) {
          return `${name.charAt(0).toUpperCase() + name.slice(1)} contains invalid characters`;
        }
        return null;
      default:
        return null;
    }
  };

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // For checkboxes, handle separately
    if (e.target.type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      const gameType = value;
      
      this.setState(prevState => {
        const currentTypes = prevState.formData.preferred_game_types || [];
        const newTypes = checked
          ? [...currentTypes, gameType]
          : currentTypes.filter(type => type !== gameType);
        
        return {
          formData: {
            ...prevState.formData,
            preferred_game_types: newTypes
          }
        };
      });
      return;
    }
    
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [name]: value
      }
    }));
    
    // Clear error when user starts typing
    const error = this.validateField(name, value);
    if (this.state.errors[name] || this.state.error) {
      this.setState(prevState => ({
        errors: {
          ...prevState.errors,
          [name]: error || ''
        },
        // Clear general error if user starts typing in a field that had an error
        error: prevState.error && (
          (name === 'display_name' && prevState.error.toLowerCase().includes('display name')) ||
          (name === 'first_name' && prevState.error.toLowerCase().includes('first name')) ||
          (name === 'last_name' && prevState.error.toLowerCase().includes('last name'))
        ) ? null : prevState.error
      }));
    }
  };

  handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.setState(prevState => ({
        errors: {
          ...prevState.errors,
          profile_pic: 'Please select a valid image file'
        }
      }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.setState(prevState => ({
        errors: {
          ...prevState.errors,
          profile_pic: 'Image size must be less than 5MB'
        }
      }));
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      this.setState({
        profilePicFile: file,
        profilePicPreview: reader.result as string,
        errors: {
          ...this.state.errors,
          profile_pic: ''
        }
      });
    };
    reader.readAsDataURL(file);
  };

  handleRemoveProfilePic = () => {
    this.setState({
      profilePicFile: null,
      profilePicPreview: DEFAULT_AVATAR,
      formData: {
        ...this.state.formData,
        profile_pic: ''
      }
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = '';
    }
  };

  validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    
    // Mandatory fields (always required per API spec)
    if (!this.state.formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!this.state.formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    if (!this.state.formData.display_name.trim()) {
      errors.display_name = 'Display name is required';
    }
    
    // For onboarding, profile mode is required
    if (this.state.isOnboarding) {
      if (!this.state.formData.profile_mode) {
        errors.profile_mode = 'Profile mode is required';
      }
    }
    
    // Validate all fields
    Object.keys(this.state.formData).forEach(key => {
      if (key !== 'preferred_game_types' && key !== 'profile_pic' && key !== 'profile_mode') {
        const error = this.validateField(key, this.state.formData[key as keyof typeof this.state.formData]);
        if (error) {
          errors[key] = error;
        }
      }
    });
    
    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!this.validateForm()) {
      return;
    }

    this.setState({ saving: true, error: null, successMessage: null });

    try {
      // Prepare update data in camelCase format for API
      // Mandatory fields: firstName, lastName, displayName
      const updateData: {
        firstName: string;
        lastName: string;
        displayName: string;
        tagline?: string;
        phone?: string;
        profilePicture?: File;
        profilePic?: string;
        profileMode?: 'public' | 'private' | 'privacy';
        city?: string;
        state?: string;
        country?: string;
        preferredGameTypes?: string[];
      } = {
        firstName: this.state.formData.first_name.trim(),
        lastName: this.state.formData.last_name.trim(),
        displayName: this.state.formData.display_name.trim()
      };

      // Optional fields
      const tagline = this.state.formData.tagline.trim();
      const phone = this.state.formData.phone.trim();
      const city = this.state.formData.city.trim();
      const state = this.state.formData.state.trim();
      const country = this.state.formData.country.trim();

      if (tagline) updateData.tagline = tagline;
      if (phone) updateData.phone = phone;
      if (this.state.formData.profile_mode) {
        updateData.profileMode = this.state.formData.profile_mode;
      }
      if (city) updateData.city = city;
      if (state) updateData.state = state;
      if (country) updateData.country = country;

      // Handle preferred game types
      if (this.state.formData.preferred_game_types.length > 0) {
        updateData.preferredGameTypes = this.state.formData.preferred_game_types;
      }

      // Handle profile picture (file takes precedence over URL)
      if (this.state.profilePicFile) {
        updateData.profilePicture = this.state.profilePicFile;
      } else if (this.state.formData.profile_pic) {
        updateData.profilePic = this.state.formData.profile_pic;
      }

      const response = await apiService.updateCustomerProfile(updateData);

      if (response.success) {
        // Update form data with response data (API returns camelCase)
        const updatedProfile = response.data || {};
        const updatedProfilePic = updatedProfile.profilePic || updatedProfile.profile_pic || this.state.formData.profile_pic;
        
        // Check if profile is now complete from API response
        const isProfileComplete = updatedProfile.isProfileComplete ?? false;

        this.setState({
          saving: false,
          successMessage: 'Profile updated successfully! Redirecting to dashboard...',
          profilePicFile: null,
          profilePicPreview: updatedProfilePic || DEFAULT_AVATAR,
          isOnboarding: !isProfileComplete, // Update onboarding status based on API response
          formData: {
            ...this.state.formData,
            profile_pic: updatedProfilePic
          }
        });

        // Redirect after successful profile update (capture navigate so timeout always has valid ref)
        const navigate = this.props.navigate;
        const dashboardPath = '/customer/friends';
        setTimeout(() => {
          try {
            const redirectPath = sessionStorage.getItem('redirectAfterLogin');
            if (redirectPath && isProfileComplete) {
              sessionStorage.removeItem('redirectAfterLogin');
              if (typeof navigate === 'function') {
                navigate(redirectPath);
              } else {
                window.location.href = redirectPath.startsWith('/') ? redirectPath : dashboardPath;
              }
            } else {
              if (typeof navigate === 'function') {
                navigate(dashboardPath);
              } else {
                window.location.href = dashboardPath;
              }
            }
          } catch (e) {
            console.error('[UserProfile] Redirect failed:', e);
            window.location.href = dashboardPath;
          }
        }, 1500);
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      const errorMessage = error.message || 'Failed to update profile. Please try again.';
      
      // Parse error message to extract field-specific errors
      const fieldErrors: { [key: string]: string } = {};
      
      // Check for display name errors
      if (errorMessage.toLowerCase().includes('display name') || 
          errorMessage.toLowerCase().includes('displayname') ||
          errorMessage.toLowerCase().includes('display_name')) {
        fieldErrors.display_name = errorMessage;
      }
      
      // Check for first name errors
      if (errorMessage.toLowerCase().includes('first name') || 
          errorMessage.toLowerCase().includes('firstname') ||
          errorMessage.toLowerCase().includes('first_name')) {
        fieldErrors.first_name = errorMessage;
      }
      
      // Check for last name errors
      if (errorMessage.toLowerCase().includes('last name') || 
          errorMessage.toLowerCase().includes('lastname') ||
          errorMessage.toLowerCase().includes('last_name')) {
        fieldErrors.last_name = errorMessage;
      }
      
      this.setState({
        saving: false,
        error: errorMessage,
        errors: {
          ...this.state.errors,
          ...fieldErrors
        }
      }, () => {
        // Auto-scroll to error message or relevant field
        this.scrollToError();
      });
    }
  };

  scrollToError = () => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      // First, try to scroll to the error message at the top (sticky, always visible)
      if (this.errorMessageRef.current) {
        this.errorMessageRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
      
      // Also scroll to the specific field if error is field-specific
      // This ensures the user sees both the error message and the problematic field
      if (this.state.errors.display_name && this.displayNameInputRef.current) {
        setTimeout(() => {
          this.displayNameInputRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }, 300);
      } else if (this.state.errors.first_name) {
        const firstnameInput = document.getElementById('first_name');
        if (firstnameInput) {
          setTimeout(() => {
            firstnameInput.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }, 300);
        }
      } else if (this.state.errors.last_name) {
        const lastnameInput = document.getElementById('last_name');
        if (lastnameInput) {
          setTimeout(() => {
            lastnameInput.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }, 300);
        }
      }
    }, 100);
  };

  componentDidUpdate(prevProps: any, prevState: UserProfileState) {
    // Scroll to error when error appears
    if (this.state.error && !prevState.error) {
      this.scrollToError();
    }
  }

  render() {
    const { loading, saving, error, successMessage, formData, errors, profilePicPreview } = this.state;

    if (loading) {
      return (
        <div className={styles.userProfileContainer}>
          <DashboardHeader />
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading profile...</p>
          </div>
          <Footer />
        </div>
      );
    }

    return (
      <div className={styles.userProfileContainer}>
        <DashboardHeader />
        <div className={styles.profileContent}>
          <div className={styles.profileCard}>
            <h1 className={styles.profileTitle}>
              {this.state.isOnboarding ? 'Complete Your Profile' : 'Update Profile'}
            </h1>
            <p className={styles.profileSubtitle}>
              {this.state.isOnboarding 
                ? 'Please fill in the required information to complete your profile setup.'
                : 'All fields are optional. Fill in the information you would like to share.'}
            </p>

            {error && (
              <div 
                ref={this.errorMessageRef}
                className={styles.errorMessage}
                role="alert"
              >
                <span className={styles.errorIcon}>✗</span> 
                <span className={styles.errorText}>{error}</span>
                <button
                  type="button"
                  onClick={() => this.setState({ error: null })}
                  className={styles.errorCloseButton}
                  aria-label="Close error message"
                >
                  ×
                </button>
              </div>
            )}

            {successMessage && (
              <div className={styles.successMessage}>
                <span>✓</span> {successMessage}
              </div>
            )}

            {/* Profile Preview Section */}
            {(formData.display_name || formData.tagline) && (
              <div className={styles.profilePreview}>
                <div className={styles.profilePreviewContent}>
                  <div className={styles.profilePreviewAvatar}>
                    <img
                      src={profilePicPreview || DEFAULT_AVATAR}
                      alt="Profile"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                      }}
                    />
                  </div>
                  <div className={styles.profilePreviewInfo}>
                    <h3 className={styles.profilePreviewName}>
                      {formData.display_name || 'Your Display Name'}
                    </h3>
                    {formData.tagline && (
                      <p className={styles.profilePreviewTagline}>
                        {formData.tagline}
                      </p>
                    )}
                    <p className={styles.profilePreviewLocation}>
                      {[formData.city, formData.state, formData.country].filter(Boolean).join(', ') || 'Location not set'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={this.handleSubmit} className={styles.profileForm}>
              {/* Profile Picture Section */}
              <div className={styles.formSection}>
                <label className={styles.sectionLabel}>
                  Profile Picture
                </label>
                <div className={styles.profilePicContainer}>
                  <div className={styles.profilePicPreview}>
                    <img
                      src={profilePicPreview || DEFAULT_AVATAR}
                      alt="Profile"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                      }}
                    />
                  </div>
                  <div className={styles.profilePicActions}>
                    <input
                      ref={this.fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={this.handleProfilePicChange}
                      className={styles.fileInput}
                      id="profilePic"
                    />
                    <label htmlFor="profilePic" className={styles.fileLabel}>
                      Choose Image
                    </label>
                    <button
                      type="button"
                      onClick={this.handleRemoveProfilePic}
                      className={styles.removePicButton}
                    >
                      Remove
                    </button>
                  </div>
                  {errors.profile_pic && (
                    <span className={styles.fieldError}>{errors.profile_pic}</span>
                  )}
                </div>
              </div>

              {/* Personal Information */}
              <div className={styles.formSection}>
                <label className={styles.sectionLabel}>Personal Information</label>
                
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="first_name">
                      First Name {this.state.isOnboarding && <span style={{color: '#ff6b6b'}}>*</span>}
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={this.handleInputChange}
                      maxLength={50}
                      className={errors.first_name ? styles.inputError : ''}
                      placeholder="Enter your first name"
                    />
                    {errors.first_name && (
                      <span className={styles.fieldError}>{errors.first_name}</span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="last_name">
                      Last Name {this.state.isOnboarding && <span style={{color: '#ff6b6b'}}>*</span>}
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={this.handleInputChange}
                      maxLength={50}
                      className={errors.last_name ? styles.inputError : ''}
                      placeholder="Enter your last name"
                    />
                    {errors.last_name && (
                      <span className={styles.fieldError}>{errors.last_name}</span>
                    )}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="display_name">
                    Display Name <span style={{color: '#ff6b6b'}}>*</span>
                  </label>
                  <input
                    ref={this.displayNameInputRef}
                    type="text"
                    id="display_name"
                    name="display_name"
                    value={formData.display_name}
                    onChange={this.handleInputChange}
                    maxLength={100}
                    className={errors.display_name ? styles.inputError : ''}
                    placeholder="Enter a display name"
                  />
                  {errors.display_name && (
                    <span className={styles.fieldError} role="alert">{errors.display_name}</span>
                  )}
                  <p style={{fontSize: '0.875rem', color: '#888', marginTop: '0.5rem'}}>
                    This name will be displayed to other users
                  </p>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="tagline">Tagline</label>
                  <input
                    type="text"
                    id="tagline"
                    name="tagline"
                    value={formData.tagline}
                    onChange={this.handleInputChange}
                    maxLength={200}
                    className={errors.tagline ? styles.inputError : ''}
                    placeholder="Enter a short tagline about yourself (optional)"
                  />
                  {errors.tagline && (
                    <span className={styles.fieldError}>{errors.tagline}</span>
                  )}
                  <p style={{fontSize: '0.875rem', color: '#888', marginTop: '0.5rem'}}>
                    A short description that appears on your profile
                  </p>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="profile_mode">
                    Profile Mode {this.state.isOnboarding && <span style={{color: '#ff6b6b'}}>*</span>}
                  </label>
                  <select
                    id="profile_mode"
                    name="profile_mode"
                    value={formData.profile_mode}
                    onChange={this.handleInputChange}
                    className={errors.profile_mode ? styles.inputError : ''}
                  >
                    <option value="public">Public - Anyone can view my profile</option>
                    <option value="private">Private - Only friends can view my profile</option>
                  </select>
                  {errors.profile_mode && (
                    <span className={styles.fieldError}>{errors.profile_mode}</span>
                  )}
                  <p style={{fontSize: '0.875rem', color: '#888', marginTop: '0.5rem'}}>
                    Choose who can see your profile information
                  </p>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={this.handleInputChange}
                    maxLength={15}
                    className={errors.phone ? styles.inputError : ''}
                    placeholder="+1234567890"
                  />
                  {errors.phone && (
                    <span className={styles.fieldError}>{errors.phone}</span>
                  )}
                </div>
              </div>

              {/* Location Information */}
              <div className={styles.formSection}>
                <label className={styles.sectionLabel}>Location</label>
                
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="city">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={this.handleInputChange}
                      maxLength={100}
                      className={errors.city ? styles.inputError : ''}
                      placeholder="Enter your city"
                    />
                    {errors.city && (
                      <span className={styles.fieldError}>{errors.city}</span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="state">State</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={this.handleInputChange}
                      maxLength={100}
                      className={errors.state ? styles.inputError : ''}
                      placeholder="Enter your state"
                    />
                    {errors.state && (
                      <span className={styles.fieldError}>{errors.state}</span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="country">Country</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={this.handleInputChange}
                      maxLength={100}
                      className={errors.country ? styles.inputError : ''}
                      placeholder="Enter your country"
                    />
                    {errors.country && (
                      <span className={styles.fieldError}>{errors.country}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Preferred Game Types */}
              <div className={styles.formSection}>
                <label className={styles.sectionLabel}>Preferred Game Types</label>
                <div className={styles.checkboxGroup}>
                  {GAME_TYPES.map(gameType => (
                    <label key={gameType} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        name="preferred_game_types"
                        value={gameType}
                        checked={formData.preferred_game_types.includes(gameType)}
                        onChange={this.handleInputChange}
                      />
                      <span>{gameType}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className={styles.formActions}>
                <Link
                  to="/customer/friends"
                  className={styles.dashboardLink}
                >
                  Go to Dashboard
                </Link>
                <button
                  type="submit"
                  disabled={saving}
                  className={styles.submitButton}
                >
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </form>
            <PrivacyData />
          </div>
        </div>
        <Footer />
      </div>
    );
  }
}

export default withNavigation(UserProfile);

